import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TruckIcon, PackageIcon, CheckCircleIcon, ArrowUUpLeftIcon, CoinsIcon, PlusIcon, PencilSimpleIcon, TrashIcon, UserPlusIcon, ListChecksIcon, DownloadSimpleIcon, ScalesIcon } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const money = (n) => `₹${(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const todayStr = () => new Date().toISOString().slice(0, 10);

export default function DeliveryBoys() {
  const { user } = useAuth();
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState({ boys: [] });
  const [moneyData, setMoneyData] = useState({ boys: [] });
  const [roster, setRoster] = useState([]);
  const [activities, setActivities] = useState([]);

  const load = useCallback(async () => {
    try {
      const [d, m, r, a] = await Promise.all([
        api.get(`/delivery-boys?from_date=${from}&to_date=${to}`),
        api.get(`/delivery-boys/money?from_date=${from}&to_date=${to}`),
        api.get(`/delivery-boys/roster`),
        api.get(`/boy-activities?from_date=${from}&to_date=${to}`),
      ]);
      setData(d.data); setMoneyData(m.data);
      setRoster(r.data || []); setActivities(a.data || []);
    } catch { toast.error("Failed to load"); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const totals = data.boys.reduce((acc, b) => {
    acc.holding += b.holding;
    acc.delivered += b.delivered_range;
    acc.loaded += b.loaded_range;
    acc.new_conns += b.new_connections_range || 0;
    acc.returned += b.returned_full_range;
    acc.refills += b.refills_range || b.delivered_range || 0;
    acc.pending += b.refill_pending_verification || 0;
    return acc;
  }, { holding: 0, delivered: 0, loaded: 0, new_conns: 0, returned: 0, refills: 0, pending: 0 });

  const activityTotals = activities.reduce((acc, a) => {
    acc.deliveries += a.deliveries || 0;
    acc.new_connections += a.new_connections || 0;
    acc.invoices_confirmed += a.invoices_confirmed || 0;
    acc.payment_cleared += a.payment_cleared || 0;
    acc.payment_pending += a.payment_pending || 0;
    return acc;
  }, { deliveries: 0, new_connections: 0, invoices_confirmed: 0, payment_cleared: 0, payment_pending: 0 });

  // Per-boy Delivery-vs-Invoice variance table (aggregates from `activities` list within date range)
  const perBoy = Object.values(activities.reduce((acc, a) => {
    const k = a.delivery_boy || "—";
    if (!acc[k]) acc[k] = { name: k, deliveries: 0, invoices_confirmed: 0, new_connections: 0, payment_cleared: 0, payment_pending: 0 };
    acc[k].deliveries += a.deliveries || 0;
    acc[k].invoices_confirmed += a.invoices_confirmed || 0;
    acc[k].new_connections += a.new_connections || 0;
    acc[k].payment_cleared += a.payment_cleared || 0;
    acc[k].payment_pending += a.payment_pending || 0;
    return acc;
  }, {})).map((b) => ({ ...b, variance: b.deliveries - b.invoices_confirmed }))
       .sort((a, b) => Math.abs(b.variance) - Math.abs(a.variance));

  const downloadReportCSV = () => {
    const headers = ["Delivery Boy","Total Deliveries (Refills)","Invoices Confirmed","Variance (Delivery − Invoice)","New Connections","Payment Cleared","Payment Pending"];
    const csv = [headers.join(",")]
      .concat(perBoy.map(b => [
        `"${b.name}"`, b.deliveries, b.invoices_confirmed, b.variance,
        b.new_connections, b.payment_cleared, b.payment_pending,
      ].join(",")))
      .concat([`"TOTAL",${activityTotals.deliveries},${activityTotals.invoices_confirmed},${activityTotals.deliveries - activityTotals.invoices_confirmed},${activityTotals.new_connections},${activityTotals.payment_cleared},${activityTotals.payment_pending}`])
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `delivery-boys-report_${from}_to_${to}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-10" data-testid="delivery-boys-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 border-b border-slate-200 pb-6">
        <div>
          <div className="overline text-brand-orange-ink">Field ops</div>
          <h1 className="font-display text-4xl font-black text-brand-blue">Delivery Boys</h1>
          <p className="text-slate-500 mt-2 max-w-lg">Manage your roster and log every day's deliveries, invoice confirmations and payments — cleared or pending.</p>
        </div>
        <div className="flex gap-3">
          <div>
            <Label className="overline">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none w-40" data-testid="db-from" />
          </div>
          <div>
            <Label className="overline">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none w-40" data-testid="db-to" />
          </div>
        </div>
      </div>

      {/* Roster */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline text-brand-orange-ink">Roster</div>
            <h2 className="font-display text-2xl font-black text-brand-blue">Your delivery boys</h2>
          </div>
          <AddBoyDialog onDone={load} />
        </div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-brand-blue-tint border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Name</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Phone</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Status</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Note</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roster.map((b) => <RosterRow key={b.id} b={b} onDone={load} />)}
              {roster.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">No delivery boys yet — add one above.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Daily activity */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline text-brand-orange-ink">Daily activity</div>
            <h2 className="font-display text-2xl font-black text-brand-blue">Log a day</h2>
            <p className="text-slate-500 mt-1 text-sm">Deliveries done, invoices confirmed, and how much payment came in vs is still pending — for a specific date.</p>
          </div>
          <AddActivityDialog roster={roster} onDone={load} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border border-slate-200 bg-white mb-4">
          <ActKpi label="Refills" value={activityTotals.deliveries} testid="act-kpi-deliveries" />
          <ActKpi label="New Connections" value={activityTotals.new_connections} testid="act-kpi-new-connections" />
          <ActKpi label="Invoices Confirmed" value={activityTotals.invoices_confirmed} testid="act-kpi-invoices" />
          <ActKpi label="Payment Cleared" value={money(activityTotals.payment_cleared)} testid="act-kpi-cleared" />
          <ActKpi label="Payment Pending" value={money(activityTotals.payment_pending)} accent testid="act-kpi-pending" />
        </div>

        {/* Delivery vs Invoice Confirmed — variance report */}
        <div className="border-2 border-brand-blue bg-white p-4 mb-4" data-testid="db-variance-report">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <ScalesIcon size={22} className="text-brand-orange" weight="fill" />
            <div>
              <div className="overline text-brand-orange-ink">Report · {from} → {to}</div>
              <h3 className="font-display text-xl font-black text-brand-blue">Delivery vs Invoice Confirmed — variance</h3>
            </div>
            <div className="flex-1" />
            <div className={`px-3 py-2 border-2 ${activityTotals.deliveries - activityTotals.invoices_confirmed === 0 ? "border-emerald-600 bg-emerald-50" : "border-[#FF2400] bg-red-50"}`}>
              <div className="overline text-slate-500 text-[10px]">Total variance</div>
              <div className={`font-mono-num font-black text-lg ${activityTotals.deliveries - activityTotals.invoices_confirmed === 0 ? "text-emerald-700" : "text-[#FF2400]"}`} data-testid="db-total-variance">
                {activityTotals.deliveries - activityTotals.invoices_confirmed > 0 ? "+" : ""}
                {activityTotals.deliveries - activityTotals.invoices_confirmed}
              </div>
            </div>
            <Button onClick={downloadReportCSV} disabled={!perBoy.length}
              className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white h-9" data-testid="db-download-csv">
              <DownloadSimpleIcon size={14} className="mr-2" /> CSV
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-brand-blue-tint">
                <tr>
                  <th className="text-left p-2 uppercase text-xs tracking-wider text-brand-blue">Delivery Boy</th>
                  <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Total Deliveries</th>
                  <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Invoices Confirmed</th>
                  <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Variance</th>
                  <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">New Conn.</th>
                  <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Pmt Cleared</th>
                  <th className="text-right p-2 uppercase text-xs tracking-wider text-brand-blue">Pmt Pending</th>
                </tr>
              </thead>
              <tbody>
                {perBoy.length === 0 && <tr><td colSpan={7} className="text-center text-slate-400 py-4">No activity in this range.</td></tr>}
                {perBoy.map((b) => (
                  <tr key={b.name} className="border-b border-slate-100" data-testid={`db-variance-row-${b.name}`}>
                    <td className="p-2 font-semibold">{b.name}</td>
                    <td className="p-2 text-right font-mono-num font-semibold">{b.deliveries}</td>
                    <td className="p-2 text-right font-mono-num">{b.invoices_confirmed}</td>
                    <td className={`p-2 text-right font-mono-num font-bold ${b.variance === 0 ? "text-emerald-700" : "text-[#FF2400]"}`}>
                      {b.variance > 0 ? "+" : ""}{b.variance}
                    </td>
                    <td className="p-2 text-right font-mono-num">{b.new_connections}</td>
                    <td className="p-2 text-right font-mono-num">{money(b.payment_cleared)}</td>
                    <td className="p-2 text-right font-mono-num">{money(b.payment_pending)}</td>
                  </tr>
                ))}
                {perBoy.length > 0 && (
                  <tr className="bg-slate-50 border-t-2 border-brand-blue">
                    <td className="p-2 uppercase text-xs tracking-wider font-bold text-brand-blue">TOTAL</td>
                    <td className="p-2 text-right font-mono-num font-black">{activityTotals.deliveries}</td>
                    <td className="p-2 text-right font-mono-num font-black">{activityTotals.invoices_confirmed}</td>
                    <td className={`p-2 text-right font-mono-num font-black ${activityTotals.deliveries - activityTotals.invoices_confirmed === 0 ? "text-emerald-700" : "text-[#FF2400]"}`}>
                      {activityTotals.deliveries - activityTotals.invoices_confirmed > 0 ? "+" : ""}
                      {activityTotals.deliveries - activityTotals.invoices_confirmed}
                    </td>
                    <td className="p-2 text-right font-mono-num font-black">{activityTotals.new_connections}</td>
                    <td className="p-2 text-right font-mono-num font-black">{money(activityTotals.payment_cleared)}</td>
                    <td className="p-2 text-right font-mono-num font-black">{money(activityTotals.payment_pending)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-brand-blue-tint border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Date</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Delivery Boy</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Refills</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">New Conn.</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Invoices Confirmed</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Payment Cleared</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Payment Pending</TableHead>
                <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Note</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {activities.map((a) => (
                <TableRow key={a.id} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`activity-row-${a.id}`}>
                  <TableCell className="font-mono-num text-xs text-slate-600">{a.date}</TableCell>
                  <TableCell className="font-semibold">{a.delivery_boy}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{a.deliveries}</TableCell>
                  <TableCell className="text-right font-mono-num">{a.new_connections || 0}</TableCell>
                  <TableCell className="text-right font-mono-num">{a.invoices_confirmed}</TableCell>
                  <TableCell className="text-right font-mono-num">{money(a.payment_cleared)}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">
                    <span className={a.payment_pending > 0.01 ? "text-[#FF2400]" : "text-slate-400"}>{money(a.payment_pending)}</span>
                  </TableCell>
                  <TableCell className="text-slate-600 text-sm">{a.note || "—"}</TableCell>
                  <TableCell className="text-right">
                    <button
                      onClick={async () => {
                        if (!confirm(`Delete activity entry for ${a.delivery_boy} on ${a.date}?`)) return;
                        try { await api.delete(`/boy-activities/${a.id}`); toast.success("Deleted"); load(); }
                        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
                      }}
                      className="text-slate-400 hover:text-[#FF2400]"
                      data-testid={`activity-delete-${a.id}`}
                    >
                      <TrashIcon size={14} />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
              {activities.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-8">No activity entries in this range.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>

      {/* Auto-computed stock+money view (existing) */}
      <section>
        <div className="overline text-brand-orange-ink mb-3">Auto · from transactions &amp; collections</div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-0 border border-slate-200 bg-white">
          <Kpi label="Holding Now" value={totals.holding} icon={TruckIcon} testid="kpi-holding" />
          <Kpi label="Loaded (range)" value={totals.loaded} icon={PackageIcon} testid="kpi-loaded" />
          <Kpi label="Refills" value={totals.refills} icon={CheckCircleIcon} accent testid="kpi-refills" />
          <Kpi label="New Connections" value={totals.new_conns} icon={CheckCircleIcon} testid="kpi-new-conns" />
          <Kpi label="Return Unsold" value={totals.returned} icon={ArrowUUpLeftIcon} testid="kpi-returned" />
        </div>
      </section>

      {totals.pending > 0 && (
        <div className="border border-[#FF2400] bg-[#FF2400]/5 px-4 py-3 text-sm flex items-center gap-3" data-testid="pending-verification-banner">
          <span className="uppercase tracking-wider text-xs font-semibold text-[#FF2400]">Pending verification</span>
          <span className="text-slate-700">
            <span className="font-mono-num font-bold text-[#FF2400]">{totals.pending}</span> refill invoice{totals.pending === 1 ? "" : "s"} still unverified across all boys in this range.
          </span>
        </div>
      )}

      <section>
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-slate-500">Money · collected vs settled with shop</div>
          <SettleDialog onDone={load} />
        </div>
        <div className="border border-slate-200 bg-white overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 border-b border-slate-200">
                <TableHead className="uppercase text-xs tracking-wider">Boy</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Collected (range)</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Cash</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">GPay</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Wallet</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Settled (range)</TableHead>
                <TableHead className="text-right uppercase text-xs tracking-wider">Pending (all-time)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {moneyData.boys.map((b) => (
                <TableRow key={`m-${b.name}`} className="hover:bg-slate-50 border-b border-slate-100" data-testid={`money-row-${b.name}`}>
                  <TableCell className="font-semibold">{b.name}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">{money(b.collected_range)}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-500">{money(b.by_mode_range?.cash)}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-500">{money(b.by_mode_range?.gpay)}</TableCell>
                  <TableCell className="text-right font-mono-num text-slate-500">{money(b.by_mode_range?.wallet)}</TableCell>
                  <TableCell className="text-right font-mono-num">{money(b.settled_range)}</TableCell>
                  <TableCell className="text-right font-mono-num font-semibold">
                    <span className={b.pending > 0.01 ? "text-[#FF2400]" : "text-slate-400"}>{money(b.pending)}</span>
                  </TableCell>
                </TableRow>
              ))}
              {moneyData.boys.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">
                  No collections attached to a delivery boy yet.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </section>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className={`p-6 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-brand-blue text-white" : ""}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className={`overline ${accent ? "text-white/70" : "text-slate-500"}`}>{label}</span>
        <Icon size={16} className={accent ? "text-white/70" : "text-slate-400"} />
      </div>
      <div className="font-mono-num text-3xl font-bold mt-3">{value}</div>
    </div>
  );
}

function ActKpi({ label, value, accent, testid }) {
  return (
    <div className={`p-5 border-r border-b border-slate-200 last:border-r-0 ${accent ? "bg-brand-blue text-white" : ""}`} data-testid={testid}>
      <div className={`overline ${accent ? "text-white/70" : "text-brand-orange-ink"}`}>{label}</div>
      <div className="font-mono-num text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}

function RosterRow({ b, onDone }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(b.name);
  const [phone, setPhone] = useState(b.phone || "");
  const [note, setNote] = useState(b.note || "");
  const [active, setActive] = useState(!!b.active);

  const save = async () => {
    try {
      await api.patch(`/delivery-boys/roster/${b.id}`, { name, phone, note, active });
      toast.success("Updated"); setEditing(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!confirm(`Remove delivery boy "${b.name}"?`)) return;
    try { await api.delete(`/delivery-boys/roster/${b.id}`); toast.success("Deleted"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (editing) {
    return (
      <TableRow className="border-b border-slate-100 bg-brand-orange-tint" data-testid={`roster-row-${b.id}`}>
        <TableCell><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-none h-8" data-testid={`roster-name-${b.id}`} /></TableCell>
        <TableCell><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none h-8 font-mono-num" /></TableCell>
        <TableCell>
          <Select value={active ? "y" : "n"} onValueChange={(v) => setActive(v === "y")}>
            <SelectTrigger className="rounded-none h-8 w-32"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="y">Active</SelectItem>
              <SelectItem value="n">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </TableCell>
        <TableCell><Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none h-8" /></TableCell>
        <TableCell className="text-right">
          <div className="inline-flex gap-2">
            <Button size="sm" onClick={save} className="h-7 rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white" data-testid={`roster-save-${b.id}`}>Save</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-7 rounded-none">Cancel</Button>
          </div>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow className="hover:bg-slate-50 border-b border-slate-100" data-testid={`roster-row-${b.id}`}>
      <TableCell className="font-semibold text-brand-blue">
        <Link to={`/delivery-boys/${b.id}`} className="hover:text-brand-orange-ink underline-offset-2 hover:underline" data-testid={`roster-open-${b.id}`}>
          {b.name}
        </Link>
      </TableCell>
      <TableCell className="font-mono-num text-xs">{b.phone || "—"}</TableCell>
      <TableCell>
        <span className={`inline-flex px-2 py-0.5 border text-xs uppercase tracking-wider ${b.active ? "border-brand-blue text-brand-blue" : "border-slate-300 text-slate-400"}`}>
          {b.active ? "Active" : "Inactive"}
        </span>
      </TableCell>
      <TableCell className="text-slate-600 text-sm">{b.note || "—"}</TableCell>
      <TableCell className="text-right">
        <div className="inline-flex gap-2">
          <button onClick={() => setEditing(true)} className="text-slate-400 hover:text-brand-blue" data-testid={`roster-edit-${b.id}`}><PencilSimpleIcon size={14} /></button>
          <button onClick={remove} className="text-slate-400 hover:text-[#FF2400]" data-testid={`roster-delete-${b.id}`}><TrashIcon size={14} /></button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function AddBoyDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/delivery-boys/roster", { name: name.trim(), phone, note, active: true });
      toast.success("Delivery boy added");
      setOpen(false); setName(""); setPhone(""); setNote("");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white" data-testid="add-boy-button">
          <UserPlusIcon size={16} className="mr-2" /> Add delivery boy
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display text-brand-blue">New delivery boy</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-boy-form">
          <div><Label className="overline">Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={60} className="rounded-none" data-testid="boy-name-input" /></div>
          <div><Label className="overline">Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="rounded-none font-mono-num" data-testid="boy-phone-input" /></div>
          <div><Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" /></div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="boy-submit-button">Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddActivityDialog({ roster, onDone }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([]);   // one per active boy

  const buildRows = () => {
    const active = roster.filter((b) => b.active !== false);
    setRows(active.map((b) => ({
      delivery_boy: b.name,
      boy_id: b.id,
      deliveries: "0",
      new_connections: "0",
      invoices_confirmed: "0",
      payment_cleared: "0",
      payment_pending: "0",
      note: "",
    })));
  };

  useEffect(() => {
    if (open) buildRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roster]);

  const setCell = (i, patch) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const totals = rows.reduce(
    (acc, r) => {
      acc.deliveries += parseInt(r.deliveries) || 0;
      acc.new_connections += parseInt(r.new_connections) || 0;
      acc.invoices_confirmed += parseInt(r.invoices_confirmed) || 0;
      acc.payment_cleared += parseFloat(r.payment_cleared) || 0;
      acc.payment_pending += parseFloat(r.payment_pending) || 0;
      return acc;
    },
    { deliveries: 0, new_connections: 0, invoices_confirmed: 0, payment_cleared: 0, payment_pending: 0 }
  );

  const submit = async (e) => {
    e.preventDefault();
    const payload = rows
      .map((r) => ({
        delivery_boy: r.delivery_boy,
        date,
        deliveries: parseInt(r.deliveries) || 0,
        new_connections: parseInt(r.new_connections) || 0,
        invoices_confirmed: parseInt(r.invoices_confirmed) || 0,
        payment_cleared: parseFloat(r.payment_cleared) || 0,
        payment_pending: parseFloat(r.payment_pending) || 0,
        note: r.note || "",
      }))
      .filter(
        (r) =>
          r.deliveries || r.new_connections || r.invoices_confirmed ||
          r.payment_cleared || r.payment_pending || r.note
      );
    if (!payload.length) return toast.error("Nothing to save — every row is empty.");

    try {
      const res = await api.post("/boy-activities/bulk", { date, rows: payload });
      toast.success(`Saved ${res.data.inserted} boy activity entr${res.data.inserted === 1 ? "y" : "ies"}`);
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-none bg-brand-orange hover:bg-brand-orange-ink text-white" data-testid="add-activity-button">
          <ListChecksIcon size={16} className="mr-2" /> Log daily activity
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300 max-w-6xl">
        <DialogHeader>
          <DialogTitle className="font-display text-brand-blue">Daily activity · all delivery boys</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="add-activity-form">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none w-44" data-testid="activity-date-input" />
            </div>
            <div className="text-xs text-slate-500">
              Fill any row that has activity — leave the rest at zero. Empty rows are skipped automatically.
            </div>
          </div>

          <div className="border border-slate-200 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-brand-blue-tint border-b border-slate-200">
                  <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Delivery Boy</TableHead>
                  <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Refills</TableHead>
                  <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">New Conn.</TableHead>
                  <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Invoices Confirmed</TableHead>
                  <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Payment Cleared</TableHead>
                  <TableHead className="text-right uppercase text-xs tracking-wider text-brand-blue">Payment Pending</TableHead>
                  <TableHead className="uppercase text-xs tracking-wider text-brand-blue">Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.boy_id || i} className="border-b border-slate-100" data-testid={`bulk-activity-row-${i}`}>
                    <TableCell className="font-semibold text-brand-blue">{r.delivery_boy}</TableCell>
                    {[
                      ["deliveries", "refills"],
                      ["new_connections", "new-conn"],
                      ["invoices_confirmed", "invoices"],
                    ].map(([field, tid]) => (
                      <TableCell key={field} className="text-right">
                        <Input
                          type="number" min="0"
                          value={r[field]}
                          onChange={(e) => setCell(i, { [field]: e.target.value })}
                          className="rounded-none font-mono-num h-8 w-24 ml-auto text-right"
                          data-testid={`bulk-${tid}-${i}`}
                        />
                      </TableCell>
                    ))}
                    <TableCell className="text-right">
                      <Input type="number" step="0.01" min="0" value={r.payment_cleared}
                        onChange={(e) => setCell(i, { payment_cleared: e.target.value })}
                        className="rounded-none font-mono-num h-8 w-28 ml-auto text-right"
                        data-testid={`bulk-cleared-${i}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input type="number" step="0.01" min="0" value={r.payment_pending}
                        onChange={(e) => setCell(i, { payment_pending: e.target.value })}
                        className="rounded-none font-mono-num h-8 w-28 ml-auto text-right"
                        data-testid={`bulk-pending-${i}`} />
                    </TableCell>
                    <TableCell>
                      <Input value={r.note}
                        onChange={(e) => setCell(i, { note: e.target.value })}
                        placeholder="Optional"
                        className="rounded-none h-8" data-testid={`bulk-note-${i}`} />
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">
                    No active delivery boys in the roster.
                  </TableCell></TableRow>
                )}
                {rows.length > 0 && (
                  <TableRow className="bg-slate-50 border-t-2 border-brand-blue">
                    <TableCell className="font-bold uppercase text-xs tracking-wider text-brand-blue">Totals</TableCell>
                    <TableCell className="text-right font-mono-num font-bold" data-testid="bulk-total-refills">{totals.deliveries}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold" data-testid="bulk-total-new-conn">{totals.new_connections}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold" data-testid="bulk-total-invoices">{totals.invoices_confirmed}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold" data-testid="bulk-total-cleared">{money(totals.payment_cleared)}</TableCell>
                    <TableCell className="text-right font-mono-num font-bold text-[#FF2400]" data-testid="bulk-total-pending">{money(totals.payment_pending)}</TableCell>
                    <TableCell />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={rows.length === 0}
              className="rounded-none bg-brand-blue hover:bg-brand-blue-ink text-white"
              data-testid="activity-submit-button">
              Save all
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SettleDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [boy, setBoy] = useState("");
  const [mode, setMode] = useState("cash");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [d, setD] = useState(new Date().toISOString().slice(0, 10));

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/boy-settlements", {
        delivery_boy: boy, mode, amount: parseFloat(amount), note, date: d,
      });
      toast.success("Settlement recorded");
      setOpen(false); setBoy(""); setAmount(""); setNote("");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="rounded-none border-slate-900 h-9" data-testid="settle-button">
          <CoinsIcon size={14} className="mr-2" /> Record Settlement
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-slate-300">
        <DialogHeader><DialogTitle className="font-display">Boy → Shop settlement</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="settle-form">
          <div>
            <Label className="overline">Delivery boy</Label>
            <Input value={boy} onChange={(e) => setBoy(e.target.value)} required className="rounded-none" data-testid="settle-boy-input" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline">Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="rounded-none" data-testid="settle-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="gpay">Google Pay</SelectItem>
                  <SelectItem value="wallet">Wallet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Date</Label>
              <Input type="date" value={d} onChange={(e) => setD(e.target.value)} className="rounded-none" data-testid="settle-date-input" />
            </div>
          </div>
          <div>
            <Label className="overline">Amount (₹)</Label>
            <Input type="number" step="0.01" min="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required className="rounded-none font-mono-num" data-testid="settle-amount-input" />
          </div>
          <div>
            <Label className="overline">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" data-testid="settle-note-input" />
          </div>
          <DialogFooter>
            <Button type="submit" className="rounded-none bg-slate-900 hover:bg-black" data-testid="settle-submit-button">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
